import { useId, useState, type FormEvent } from "react";
import { NumberField, Field, Button } from "../components/rumonai";
import { platformRequest, type PlatformSetting, type PlatformSettingGroup } from "./api";
import {
  CommandFeedback, PageHeading, PlatformError, PlatformLoading, usePlatformCommand, usePlatformRead
} from "./shared";

/**
 * A field's text as the value a save sends. An emptied field goes as null,
 * never as 0, so the Hub refuses it in that setting's own words.
 */
function typed(text: string): number | null {
  const value = Number(text);
  return text.trim() === "" || !Number.isFinite(value) ? null : value;
}

/**
 * 平台设置: every setting the Hub declares, by group.
 *
 * The page knows no setting by name. It draws what `/api/admin/settings`
 * lists and sends back only the settings the admin changed, with the reason;
 * the Hub checks every value against its declaration and saves all or none.
 */
export function PlatformSettings({ scope = "runtime", compact = false }: { scope?: "runtime" | "credits"; compact?: boolean }) {
  const reading = usePlatformRead<{ groups: PlatformSettingGroup[] }>("/api/admin/settings");
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [reason, setReason] = useState(compact ? "调整新用户体验额度" : "");
  const command = usePlatformCommand();
  const data = reading.data;
  const groups = data?.groups.filter(group => (group.section ?? "runtime") === scope) ?? [];
  const changed = groups.flatMap(group => group.settings)
    .filter(setting => setting.key in draft && typed(draft[setting.key]) !== setting.value);
  const save = (event: FormEvent) => {
    event.preventDefault();
    if (!changed.length || !reason.trim()) return;
    void command.run(async () => {
      await platformRequest("/api/admin/settings", {
        method: "PUT",
        body: JSON.stringify({
          changes: Object.fromEntries(changed.map(setting => [setting.key, typed(draft[setting.key])])),
          reason: reason.trim(),
        }),
      });
      setDraft(current => Object.fromEntries(Object.entries(current).filter(([key, value]) =>
        !changed.some(setting => setting.key === key && draft[key] === value))));
      setReason(compact ? "调整新用户体验额度" : "");
      command.setNotice("平台设置已保存。");
      reading.reload();
    });
  };
  return <>
    {!compact ? <PageHeading title="运行设置"><p>资源上限、登录与公开访问规则。</p></PageHeading> : null}
    {reading.error ? <PlatformError error={reading.error} retry={reading.reload} /> : null}
    {!data && reading.loading ? <PlatformLoading label="正在读取平台设置" /> : null}
    {data && groups.length > 0 ? (
      <form className={`platform-settings${compact ? " admin-settings-compact" : ""}`} noValidate onSubmit={save}>
        {groups.map(group => (
          <fieldset key={group.key} disabled={command.pending}>
            <legend>{group.title}</legend>
            {group.note ? <p>{group.note}</p> : null}
            <div className="platform-setting-grid">
              {group.settings.map(setting => (
                <SettingField
                  key={setting.key}
                  setting={setting}
                  text={draft[setting.key]}
                  onChange={text => setDraft(old => ({ ...old, [setting.key]: text }))}
                />
              ))}
            </div>
          </fieldset>
        ))}
        <div className="platform-admin-form">
          <CommandFeedback error={command.error} notice={command.notice} />
          {!compact ? <Field className="field" label="操作原因"><input
              className="text-input"
              name="reason"
              value={reason}
              maxLength={500}
              required
              disabled={command.pending}
              placeholder="说明这次调整的原因"
              onChange={event => setReason(event.target.value)}
            /></Field> : null}
          <div className="platform-actions">
            <Button variant="primary" aria-busy={command.pending || undefined}

              type="submit"
              disabled={command.pending || !changed.length || !reason.trim()}
            >
              {command.pending ? "正在保存…" : "保存设置"}
            </Button>
            <Button variant="ghost"

              type="button"
              disabled={command.pending || !changed.length}
              onClick={() => setDraft({})}
            >
              放弃修改
            </Button>
          </div>
        </div>
      </form>
    ) : null}
  </>;
}

function SettingField({ setting, text, onChange }: {
  setting: PlatformSetting;
  /** What the admin typed; undefined while the field shows the value held. */
  text?: string;
  onChange: (text: string) => void
}) {
  const id = useId();
  const described = [setting.unit && `${id}-unit`, setting.note && `${id}-note`, `${id}-range`]
    .filter(Boolean).join(" ");
  const edited = text !== undefined && typed(text) !== setting.value;
  return <div className="platform-setting" data-changed={edited ? "" : undefined}>
    <label htmlFor={id}>{setting.title}</label>
    <div className="platform-setting-input">
      <NumberField
        id={id}
        label={setting.title}
        name={setting.key}
        inputMode={setting.type === "integer" ? "numeric" : "decimal"}
        min={setting.minimum}
        max={setting.maximum}
        step={setting.step}
        value={text ?? String(setting.value)}
        aria-describedby={described}
        onChange={event => onChange(event.target.value)}
      />
      {setting.unit ? <span id={`${id}-unit`}>{setting.unit}</span> : null}
    </div>
    {setting.note ? <small id={`${id}-note`}>{setting.note}</small> : null}
    <small id={`${id}-range`}>可设 {setting.minimum}–{setting.maximum}，默认 {setting.default}</small>
  </div>;
}
